import * as Yup from 'yup';
import { emailRegEx, phoneRegEx, websiteRegex, zipCodeRegEx } from '@/utils/RegularExpressions';
import { States } from '../../../../../constants';

export const validStateOptions: string[] = States.map((state) => state.name);
export const dealTypeOptions = [
  'Affordable Rental Housing: 4%, 9%',
  'Home Ownership',
  'Market Rate Multifamily',
  'Mixed Use',
  'Existing Operating Property',
  'Other',
];

export const constructionTypeOptions = ['New Construction', 'Historic Renovation', 'Renovation', 'Adaptive Reuse', 'In-Place Renovation'];

export const acquisitionTypeOptions = ['Off-Market', 'On-Market', 'Joint venture with land owner', 'Other'];

export const siteControlStatusOptions = [
  'LOI Obtained',
  'Purchase Contract Executed',
  'RFP Submitted',
  'Exclusive Negotiating Period Entered Into',
  'Other',
];

export const projectByRightOptions = ['Yes', 'No', 'Not Yet Determined'];

const step1Schema = Yup.object({
  projectDetails: Yup.object({
    streetAddress: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
    city: Yup.string().required('Required').min(2, 'It must be greater than 2 character'),
    state: Yup.string().required('Required').oneOf(validStateOptions, 'Invalid option'),
    zipCode: Yup.string()
      .required('Required')
      .matches(zipCodeRegEx, 'It must be 5 digits')
      .max(5, 'It must be 5 digits')
      .min(5, 'It must be 5 digits'),
    dealType: Yup.string().required('Required').oneOf(dealTypeOptions, 'Invalid option'),
    proposedUnits: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .integer('It must be integer')
      .transform((_, val) => (val ? Number(val) : undefined)),
    constructionType: Yup.string().required('Required').oneOf(constructionTypeOptions, 'Invalid option'),
    acquisitionType: Yup.string().required('Required').oneOf(acquisitionTypeOptions, 'Invalid option'),
    siteControlStatus: Yup.string().required('Required').oneOf(siteControlStatusOptions, 'Invalid option'),
    projectByRight: Yup.string().required('Required').oneOf(projectByRightOptions, 'Invalid option'),
    acquisitionCostEstimate: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .transform((_, val) => (val ? Number(val) : undefined)),
    developmentCostEstimate: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .transform((_, val) => (val ? Number(val) : undefined)),
    preDevelopmentCostEstimate: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .positive('It must be greater than 0')
      .transform((_, val) => (val ? Number(val) : undefined)),
    moreInfo: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
  }),
});

const step2Schema = Yup.object({
  developmentTeam: Yup.array(),
});

const step3Schema = Yup.object({
  developmentSchedule: Yup.object({
    schedule1: Yup.object({
      startDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        }),
      finishDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        }),
    }),
    schedule2: Yup.object({
      startDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        }),
      finishDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        }),
    }),
    schedule3: Yup.object({
      startDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        }),
      finishDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        }),
    }),
    schedule4: Yup.object({
      startDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        }),
      finishDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        }),
    }),
    schedule5: Yup.object({
      startDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be earlier than finish date',
          test: (val, ctx) => {
            const { finishDate } = ctx.parent;
            if (finishDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) < new Date(finishDate);
            }
            return true;
          },
        }),
      finishDate: Yup.date()
        .required('Required')
        .typeError('Invalid date')
        .test({
          message: 'It must be later than start date',
          test: (val, ctx) => {
            const { startDate } = ctx.parent;
            if (startDate.toString() !== 'Invalid Date' && val) {
              return new Date(val) > new Date(startDate);
            }
            return true;
          },
        }),
    }),
  }),
});

const step4Schema = Yup.object({
  applicantInformation: Yup.object({
    entityName: Yup.string().required('Required').min(2, 'It must be greater than 2 characters'),
    ein: Yup.string().required('Required').min(9, 'It must be 9 digits').max(9, 'It must be 9 digits'),
    contactPersonName: Yup.string().required('Required').min(2, 'It must be greater than 2 characters'),
    contactPersonEmail: Yup.string().required('Required').matches(emailRegEx, 'Invalid E-mail Address'),
    streetAddress: Yup.string().required('Required').min(4, 'It must be greater than 4 character'),
    city: Yup.string().required('Required').min(2, 'It must be greater than 2 character'),
    state: Yup.string().required('Required').oneOf(validStateOptions, 'Invalid option'),
    zipCode: Yup.string()
      .required('Required')
      .matches(zipCodeRegEx, 'It must be 5 digits')
      .max(5, 'It must be 5 digits')
      .min(5, 'It must be 5 digits'),
    phone: Yup.string()
      .required('Required')
      .typeError('Number is required')
      .matches(phoneRegEx, 'It must be 10 digits')
      .min(10, 'It must be 10 digits')
      .max(10, 'It must be 10 digits'),
    website: Yup.string()
      .matches(websiteRegex, 'Invalid website format')
      .nullable()
      .transform((v) => (v === '' ? null : v)),
    formationDate: Yup.date().required('Required').typeError('Invalid date').max(new Date(), 'It cannot be in future'),
    fullTimeEmployeeCount: Yup.number()
      .required('Required')
      .typeError('Number is required')
      .integer('It must be integer')
      .positive('It cannot be negative')
      .min(1, 'It must be greater than 1'),
  }),
});

const step5Schema = Yup.object({
  experience: Yup.object({
    companyCompleted: Yup.array(),
    principalCompleted: Yup.array(),
  }),
});

export const BTPPValidationSchema = [step1Schema, step2Schema, step3Schema, step4Schema, step5Schema];
